import { dateKey } from '@/services/review/reviewAlgorithm.js'
import { isAnswerCorrect } from './questionAnswer.js'
import type { Question } from '@/types/knowledge.js'
import type { QuestionAttempt, UserDatabase, WrongQuestionRecord } from '@/types/user-data.js'

export interface QuestionAnswerSubmission {
  sessionId: string
  attemptId: string
  answeredAt?: Date
  durationMs?: number
}

function sameAttempt(left: QuestionAttempt, right: QuestionAttempt): boolean {
  return JSON.stringify(left) === JSON.stringify(right)
}

export function applyQuestionAnswer(
  database: UserDatabase,
  question: Question,
  selectedAnswer: readonly string[],
  submission: QuestionAnswerSubmission,
): UserDatabase {
  const session = database.practiceSessions[submission.sessionId]
  if (!session) throw new Error(`找不到练习 Session ${submission.sessionId}。`)

  const answeredAt = submission.answeredAt ?? new Date()
  const timestamp = answeredAt.toISOString()
  const correct = isAnswerCorrect(question, selectedAnswer)
  const attempt: QuestionAttempt = {
    id: submission.attemptId,
    questionId: question.id,
    knowledgeId: question.knowledgeId,
    sessionId: session.id,
    answeredAt: timestamp,
    userAnswer: [...selectedAnswer],
    correctAnswer: [...question.answer],
    isCorrect: correct,
    mode: session.mode,
    ...(session.category ? { category: session.category } : {}),
    ...(submission.durationMs === undefined ? {} : { durationMs: submission.durationMs }),
    contentVersion: database.contentVersion,
  }
  const existingAttempt = database.questionAttempts[submission.attemptId]
  if (existingAttempt) {
    if (sameAttempt(existingAttempt, attempt)) return database
    throw new Error(`答题记录 ${submission.attemptId} 已存在，不能覆盖。`)
  }
  if (session.finishedAt) throw new Error(`练习 Session ${session.id} 已结束。`)
  const questionIndex = session.attemptIds.length
  if (session.questionIds[questionIndex] !== question.id) {
    throw new Error('提交的题目与练习 Session 当前题目不一致。')
  }

  const statisticDate = dateKey(answeredAt)
  const existingStatistic = database.dailyStatistics.find(
    (statistic) => statistic.date === statisticDate,
  )
  const nextStatistics = existingStatistic
    ? database.dailyStatistics.map((statistic) =>
        statistic.date === statisticDate
          ? {
              ...statistic,
              answered: statistic.answered + 1,
              correct: statistic.correct + (correct ? 1 : 0),
            }
          : statistic,
      )
    : [
        ...database.dailyStatistics,
        {
          date: statisticDate,
          learned: 0,
          reviewed: 0,
          answered: 1,
          correct: correct ? 1 : 0,
        },
      ]

  const previousRecord = database.wrongQuestions[question.id]
  const currentProgress = database.progress[question.knowledgeId] ?? {
    knowledgeId: question.knowledgeId,
    masteryLevel: 0 as const,
    studyCount: 0,
    favorite: false,
    wrongCount: 0,
  }
  const nextProgress = correct
    ? currentProgress
    : {
        ...currentProgress,
        wrongCount: currentProgress.wrongCount + 1,
        // A wrong answer makes this knowledge point immediately eligible for review.
        nextReviewAt: timestamp,
      }

  const nextWrongQuestions: Record<string, WrongQuestionRecord> = {
    ...database.wrongQuestions,
  }
  if (correct && previousRecord) {
    nextWrongQuestions[question.id] = { ...previousRecord, mastered: true }
  } else if (!correct) {
    nextWrongQuestions[question.id] = {
      questionId: question.id,
      knowledgeId: question.knowledgeId,
      selectedAnswer: [...selectedAnswer],
      wrongCount: (previousRecord?.wrongCount ?? 0) + 1,
      lastWrongAt: timestamp,
      mastered: false,
    }
  }

  return {
    ...database,
    progress: {
      ...database.progress,
      [question.knowledgeId]: nextProgress,
    },
    wrongQuestions: nextWrongQuestions,
    questionAttempts: {
      ...database.questionAttempts,
      [attempt.id]: attempt,
    },
    practiceSessions: {
      ...database.practiceSessions,
      [session.id]: {
        ...session,
        attemptIds: [...session.attemptIds, attempt.id],
        answeredCount: session.answeredCount + 1,
        correctCount: session.correctCount + (correct ? 1 : 0),
        wrongCount: session.wrongCount + (correct ? 0 : 1),
        ...(session.answeredCount + 1 === session.totalCount ? { finishedAt: timestamp } : {}),
      },
    },
    dailyStatistics: nextStatistics,
    recentKnowledgeId: question.knowledgeId,
  }
}
